import { hasSubscriptionFlag } from '@/data/flags/local-flags';
import { i18n_t } from '@utils/i18n';
import type { BlacklistTypes } from '@pendo/aggregations';

/**
 * Store-free, typed copies of the default-segment helpers from `./segments.js`, used by the funnel
 * report path so it doesn't pull the Vuex store (and its i18n / @pendo/services chain) just to parse
 * segment params. `segments.js` imports the store at module scope and keeps its own copies for its
 * existing consumers; these are intentionally kept in sync by hand for now. New store-free callers
 * should import from here.
 */

const $t = i18n_t;

type SegmentPipeline =
    | null
    | { operator: { identified: string } }
    | Array<{ source?: { visitors: null }; filter?: string }>;

export interface DefaultSegment {
    order: number;
    default: boolean;
    name: string;
    aside?: string;
    id: string;
    shared: boolean;
    definition: string;
    description?: string;
    blacklist?: BlacklistTypes;
    enabled: () => Promise<DefaultSegment | null>;
    pipeline: () => SegmentPipeline;
}

export interface SegmentParamsInput {
    segmentId?: string;
}

export const getDefaultSegments = (): Record<string, DefaultSegment> => ({
    everyone: {
        order: 1,
        default: true,
        name: $t('segments.default.Everyone'),
        id: 'everyone',
        shared: true,
        definition: 'everyone',
        description: $t(
            'segments.default.everyone.Includes all visitors to any app in the subscription, both identified and anonymous',
        ),
        enabled () {
            return Promise.resolve(this);
        },
        pipeline () {
            return null;
        },
    },
    everyoneExcludingAnonymous: {
        order: 2,
        default: true,
        name: $t('segments.default.Everyone'),
        aside: $t('segments.default.aside.identifiedVisitorsOnly'),
        id: 'everyoneExcludingAnonymous',
        shared: true,
        definition: 'everyoneExcludingAnonymous',
        description: $t(
            'segments.default.everyoneExcludingAnonymous.Includes only known visitors with an assigned Visitor ID',
        ),
        enabled () {
            const showAnonymous = hasSubscriptionFlag('showAnonymous');

            return Promise.resolve(showAnonymous ? this : null);
        },
        pipeline () {
            return {
                operator: {
                    identified: 'visitorId',
                },
            };
        },
    },
    anonymousVisitorsOnly: {
        order: 3,
        default: true,
        name: $t('segments.default.Everyone'),
        aside: $t('segments.default.aside.anonymousVisitorsOnly'),
        id: 'anonymousVisitorsOnly',
        shared: true,
        definition: 'anonymousVisitorsOnly',
        description: $t(
            'segments.default.anonymousVisitorsOnly.Includes only anonymous visitors who have not been mapped to a Visitor ID',
        ),
        enabled () {
            const showAnonymous = hasSubscriptionFlag('showAnonymous');

            return Promise.resolve(showAnonymous ? this : null);
        },
        pipeline () {
            return {
                operator: {
                    identified: '!visitorId',
                },
            };
        },
    },
    blacklistOnly: {
        order: 4,
        default: true,
        get name () {
            return hasSubscriptionFlag('enableAccounts')
                ? $t('segments.default.Excluded Accounts & Visitors')
                : $t('segments.default.Excluded Visitors');
        },
        id: 'blacklistOnly',
        shared: true,
        definition: 'blacklistOnly',
        get description () {
            return hasSubscriptionFlag('enableAccounts')
                ? $t(
                    'segments.default.blacklistOnly.Visitors and accounts that are part of the Exclude List in your subscription settings',
                )
                : $t(
                    'segments.default.blacklistOnly.Visitors that are part of the Exclude List in your subscription settings',
                );
        },
        blacklist: 'only',
        async enabled () {
            return Promise.resolve(this);
        },
        pipeline () {
            return null;
        },
    },
    noOne: {
        order: 5,
        default: true,
        name: $t('segments.default.No one'),
        id: 'noOne',
        shared: true,
        definition: 'noOne',
        enabled () {
            return Promise.resolve(this);
        },
        pipeline () {
            return [
                {
                    source: {
                        visitors: null,
                    },
                },
                {
                    filter: 'false',
                },
            ];
        },
    },
});

export function getBlacklistFromSegment (segmentId: string | undefined): BlacklistTypes {
    const segment = segmentId ? getDefaultSegments()[segmentId] : undefined;

    return segment?.blacklist || 'apply';
}

export function isDefaultSegment (segmentId: string | undefined): boolean {
    return !!(segmentId && getDefaultSegments()[segmentId]);
}

export function getIdentify (segmentId: string | undefined): string | null {
    const showAnonymous = hasSubscriptionFlag('showAnonymous');

    if (showAnonymous) {
        switch (segmentId) {
            case getDefaultSegments().everyoneExcludingAnonymous.id:
                return 'visitorId';
            case getDefaultSegments().anonymousVisitorsOnly.id:
                return '!visitorId';
            default:
                return null;
        }
    }

    return 'visitorId';
}

export function getIncludeDNT (segmentId: string | undefined): boolean | undefined {
    if (segmentId === getDefaultSegments().everyone.id) {
        return true;
    }

    return undefined;
}

export function parseSegmentParams<T extends SegmentParamsInput> (params: T) {
    const { segmentId } = params;

    return {
        blacklist: getBlacklistFromSegment(segmentId),
        includeAnonymous: getIdentify(segmentId),
        includeDNT: getIncludeDNT(segmentId),
        ...params,
        segmentId: isDefaultSegment(segmentId) ? null : segmentId,
    };
}
